/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable react/no-unstable-nested-components */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReturnPicking, useReturnsQuery } from 'src/services/returns';
import { createColumnHelper } from '@tanstack/react-table';

import { ReturnStateBadge } from 'src/components/layouts/ReturnsTable/ReturnsTable';
import { formatDate, formatTime, getCountryImageSource } from 'src/utils/general';
import { useNavigate } from 'react-router-dom';
import Icon from 'src/components/ui/Icon/Icon';
import { useQuickFilterQuery } from 'src/services/utility';
import { useNavigationItems } from '../hooks/useNavigationItems';
import Checkbox from '../components/ui/Checkbox/Checkbox';
import { DataView } from '../components/ui/TableOverview/DataView';
import NoDataPlaceholder from '../components/layouts/NoDataPlaceholder/NoDataPlaceholder';
import { useDataViewController } from '../hooks/useDataViewController';
import { SearchModels } from '../components/ui/TableOverview/utils/SearchFieldRegistry';
import { OverlayNavProvider } from '../providers/OverlayNavProvider';

function Returns() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: quickFilters } = useQuickFilterQuery('stock.picking.return');

  const { findAccessRights } = useNavigationItems();
  const accessRights = findAccessRights('returns');

  const columnHelper = createColumnHelper<ReturnPicking>();
  const columns = [
    columnHelper.accessor(row => row.name, {
      id: 'name',
      size: 200,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.name'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14--bold text-full truncate"> {info.row.original.partner?.name} </span>
          <span className="typo-text-12 text-faded truncate"> {info.getValue()} </span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.sale_order?.name, {
      id: 'sale_order',
      size: 200,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('returns.returned_order'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-12 text-faded truncate"> {info.getValue()} </span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.date, {
      id: 'date',
      size: 200,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.date'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14--bold text-full truncate">
            {info.row.original.date && formatDate(info.row.original.date)}
          </span>
          <span className="typo-text-12 text-faded truncate">
            {info.row.original.date && formatTime(info.row.original.date)}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.partner?.address.city, {
      id: 'partner_street',
      size: 200,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.address'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14 truncate">{info.row.original.partner?.address.street}</span>
          <div className="flex gap-2 w-full">
            <span className="typo-text-12 text-faded">{info.row.original.partner?.address.zip_code}</span>
            <span className="typo-text-12 text-faded">{info.getValue()}</span>
          </div>
        </div>
      ),
    }),
    columnHelper.accessor(row => row.partner?.address.country_code, {
      id: 'partner_country',
      size: 100,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.country'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const icon = getCountryImageSource(info.getValue());
        return (
          <div className="flex gap-8 items-center">
            {icon && <Icon name={icon} width={20} height={20} />}
            <span className="typo-text-14 text-full truncate">{info.getValue()}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor(row => row.status, {
      id: 'state',
      size: 100,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.status'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const status = info.getValue();
        return (
          <div className="flex">
            <span className="typo-text-14 text-full capitalize">{status && <ReturnStateBadge status={status} />}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor(row => row.refunded, {
      id: 'refunded',
      size: 100,
      enableHiding: true,
      enableSorting: true,
      meta: {
        label: t('general.refunded'),
      },
      header: ({ column }) => (
        <span className="typo-text-12 text-faded flex items-center justify-center w-full">
          {column.columnDef.meta?.label}
        </span>
      ),
      cell: info => {
        return (
          <div className="flex items-center justify-center">
            <Checkbox checked={info.getValue() ?? false} disabled />
          </div>
        );
      },
    }),
  ];

  const tabs = quickFilters?.map(quickFilter => ({
    label: quickFilter.label,
    key: quickFilter.key,
  }));

  const controller = useDataViewController({
    sorting: [
      {
        id: 'date',
        desc: true,
      },
    ],
  });
  const [entityIds, setEntityIds] = useState<(string | number)[]>([]);

  return tabs ? (
    <OverlayNavProvider entityIds={entityIds} basePath="/returns">
      <DataView<ReturnPicking>
        onDataLoaded={items => setEntityIds(items.map(i => i.id).filter((id): id is number => id != null))}
        controller={controller}
        modelKey={SearchModels.Return}
        filterModelId="stock.picking"
        headline={t('navbar.returns')}
        emptyState={<NoDataPlaceholder headline={t('general.empty_returns')} />}
        allowBulkActions={accessRights?.read_write}
        tabs={[
          {
            label: t('integrations.all'),
            key: 'all',
            value: quickFilters?.map(q => q.value).reduce((a, b) => a + b, 0),
          },
          ...(quickFilters ?? []),
        ]}
        getDomainForTab={(tabKey, index) => (index === 0 ? '[]' : `[("return_state", "=", "${tabKey}")]`)}
        actions={[
          {
            title: t('returns.new_return'),
            icon: 'plus',
            onExecute: () => {
              navigate('/returns/create/tracking');
            },
            showInToolbar: accessRights?.read_write,
          },
          {
            title: t('general.view_details'),
            icon: 'edit',
            isRowClickAction: true,
            onExecute: selected => {
              if (selected.length > 0) {
                navigate(`/returns/${selected[0].id}`);
              }
            },
            showInContextMenu: true,
          },
        ]}
        kpiModel="stock.picking.return"
        columns={columns}
        useDataQuery={useReturnsQuery}
      />
    </OverlayNavProvider>
  ) : undefined;
}

export default Returns;
